import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IntegerQueryItemDto } from 'src/core/models/query/integer-query-item.dto';
import { QueryEditorBase } from 'src/core/models/query/query-editor-base';
import { LocalizationService } from '@abp/ng.core';
import { LanguageCodeDto } from 'src/core/models/language-code/language-code.dto';
import { QueryService } from 'src/core/services/query/query.service';
import { Operators } from 'src/core/models/request/operator.enum';
import { CrudService } from 'src/core/services/crud/crud.service';

@Component({
  selector: 'ca-query-tree-node-language',
  templateUrl: './query-tree-node-language.component.html',
  styleUrls: ['./query-tree-node-language.component.scss'],
  standalone: false,
})
export class QueryTreeNodeLanguageComponent
  extends QueryEditorBase
  implements OnInit, AfterViewInit
{
  languageForm: FormGroup;
  payload: IntegerQueryItemDto;
  multipleValuePayloads: IntegerQueryItemDto[] = [];
  reservedIds: number[] = [];
  reservedInternalIds: number[] = [];
  information: string;
  isInitialLoad: boolean = true;
  loading = true;
  languages: LanguageCodeDto[] = [];

  get languageNameList(): string {
    return this.selectedLanguages.map(x => x.name).join('\r\n');
  }

  get selectedLanguages(): LanguageCodeDto[] {
    const ids = [this.payload?.value, ...this.multipleValuePayloads.map(x => x.value)].filter(
      id => id != null,
    );
    return this.languages.filter(l => ids.includes(l.id));
  }

  get selectedLanguageNames(): string {
    return this.selectedLanguages
      .map(x => this.localizationService.instant('Settings::Call.Language.' + x.name))
      .join(', ');
  }

  constructor(
    private fb: FormBuilder,
    private localizationService: LocalizationService,
    private queryService: QueryService,
    private operators: Operators,
    private crudService: CrudService,
  ) {
    super();

    this.information = this.localizationService.instant('::ThisFieldIsRequired');

    this.languageForm = this.fb.group(
      {
        language: [null, { validators: [Validators.required], updateOn: 'change' }],
      },
      { updateOn: 'blur' },
    );

    this.languageForm.get('language').valueChanges.subscribe((val: number[]) => {
      this.onChange(val);
    });

    this.languageForm.statusChanges.subscribe(status => {
      this.node.validationStatus = status === 'VALID';
    });
  }

  operatorChanged() {
    this.node.isDirty = true;
  }

  ngOnInit() {
    this.payload = this.node.categoryItem.payload as IntegerQueryItemDto;
    this.multipleValuePayloads = Array.isArray(this.node.categoryItem.multipleValuePayloads)
      ? this.node.categoryItem.multipleValuePayloads.map(x => x as IntegerQueryItemDto)
      : [];

    this.reservedIds = this.multipleValuePayloads.map(x => x.id);
    this.reservedInternalIds = this.multipleValuePayloads.map(x => x.internalId);
  }

  ngAfterViewInit() {
    this.queryService.getQueryLanguageDefinitions().subscribe(response => {
      this.languages = response;

      if (this.payload.value) {
        const values: number[] = [
          this.payload.value,
          ...this.multipleValuePayloads.map(x => x.value),
        ];
        this.languageForm.get('language').setValue(values);
      }

      this.loading = false;
    });
  }

  onChange(selectedLanguageIds: number[]) {
    if (Array.isArray(selectedLanguageIds) && selectedLanguageIds.length >= 1) {
      this.payload.value = selectedLanguageIds[0];
    }

    if (this.isInitialLoad) {
      this.isInitialLoad = false;
    } else {
      this.node.isDirty = true;
    }

    if (Array.isArray(selectedLanguageIds) && selectedLanguageIds.length > 0) {
      const copyOfReservedIds = [...this.reservedIds];
      const copyOfReservedInternalIds = [...this.reservedInternalIds];

      this.multipleValuePayloads = [];

      for (let i = 1; i < selectedLanguageIds.length; i++) {
        const reservedId = copyOfReservedIds[i - 1];
        const reservedInternalId = copyOfReservedInternalIds[i - 1];

        const newPayload = Object.assign({}, this.payload);

        newPayload.internalId = reservedInternalId
          ? reservedInternalId
          : this.queryService.getInternalId();
        newPayload.parentInternalId = this.payload.internalId;
        newPayload.multiple = true;
        newPayload.value = selectedLanguageIds[i];
        newPayload.id = reservedId ? reservedId : 0;

        this.multipleValuePayloads.push(newPayload);
      }

      this.node.categoryItem.multipleValuePayloads = new Array(...this.multipleValuePayloads);
    }
  }
}
